<script setup>
  import { computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { CheckCheck } from "lucide-vue-next";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Label } from "@/components/ui/label";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Button } from "@/components/ui/button";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
  import { useMGroupStore } from "@stores/mGroup";

  const mGroupStore = useMGroupStore();
  const { mGroup, activeCreatives, activeAudiences, selectedCreatives, selectedAudiences } = storeToRefs(mGroupStore);
  const { isAdsSelected, areSomeSelected, selectAds, clearAds } = mGroupStore;

  const selectAllAds = () => selectAds(true, null, null);
  const clearAllAds = () => selectAds(false, null, null);

  const audienceOptions = computed(() =>
    activeAudiences.value.map((a) => ({ value: a.token, label: a.name })),
  );
  const creativeOptions = computed(() =>
    activeCreatives.value.map((c) => ({ value: c.token, label: c.name })),
  );

  const checkboxState = (aToken, cToken) =>
    isAdsSelected(aToken, cToken) ? true : areSomeSelected(aToken, cToken) ? "indeterminate" : false;

  const toggle = (value, aToken, cToken) => selectAds(value, aToken, cToken);

  const cAds = (a, c) =>
    a.m_dimensions
      .filter((d) => d.status === "active")
      .reduce((p, d) => p * (d.m_segments.filter((s) => s.status === "active").length || 1), 1) *
    (c.template_ids?.length || 1) *
    (c.media_type === "video" ? 1 : c.m_medium_upload_ids?.length || 1);

  const sumPairs = (predicate = () => true) =>
    selectedAudiences.value.reduce(
      (s, a) =>
        s + selectedCreatives.value.filter((c) => predicate(a, c)).reduce((t, c) => t + cAds(a, c), 0),
      0,
    );

  const activeAdsCount = computed(() => sumPairs((a, c) => isAdsSelected(a.token, c.token)));
  const totalAdsCount = computed(() => sumPairs());

  watch(
    [() => mGroup.value.selected_creatives, () => mGroup.value.selected_audiences],
    () => clearAds(),
  );
</script>

<template>
  <Card class="p-6 max-h-[calc(100vh-60px-40px)] flex flex-col">
    <CardHeader class="pb-6">
      <CardTitle class="heading-page truncate">{{ mGroup.name }}</CardTitle>
    </CardHeader>
    <CardContent class="flex-1 min-h-0 overflow-y-auto">
      <form
        class="space-y-6"
        @submit.prevent>
        <div class="space-y-1.5">
          <Label>Name</Label>
          <Input v-model="mGroup.name" />
        </div>
        <div class="grid grid-cols-2 gap-4 items-start">
          <div class="space-y-1.5">
            <Label>Audiences</Label>
            <Select
              v-model="mGroup.selected_audiences"
              :options="audienceOptions"
              multiple
              filterable />
          </div>
          <div class="space-y-1.5">
            <Label>Creatives</Label>
            <Select
              v-model="mGroup.selected_creatives"
              :options="creativeOptions"
              multiple
              filterable />
          </div>
        </div>
        <div class="space-y-1.5">
          <Label>Selected Ads</Label>
          <div class="border rounded-md overflow-hidden">
            <div class="flex items-center justify-between gap-2 px-3 py-2 border-b bg-background">
              <div class="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  @click="selectAllAds">
                  <CheckCheck />
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  @click="clearAllAds">
                  Clear
                </Button>
              </div>
              <div class="text-sm">
                <span class="font-semibold">{{ activeAdsCount }}</span>
                <span class="text-muted-foreground"> / {{ totalAdsCount }} ads active</span>
              </div>
            </div>
            <div class="relative overflow-auto max-h-[600px]">
              <Table>
                <TableHeader class="sticky top-0 z-20 bg-muted/60">
                  <TableRow class="divide-x divide-border">
                    <TableHead class="sticky left-0 z-30 bg-muted/60 w-[220px] border-r">
                      <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Creatives \ Audiences
                      </span>
                    </TableHead>
                    <TableHead
                      v-for="audience in selectedAudiences"
                      :key="`audience-column-${audience.token}`"
                      class="text-center min-w-[120px] py-2 bg-muted/60">
                      <div class="flex flex-col items-center gap-1">
                        <Checkbox
                          :model-value="checkboxState(audience.token, null)"
                          @update:model-value="(v) => toggle(v, audience.token, null)" />
                        <Tooltip :content="audience.name">
                          <div class="text-sm font-medium truncate max-w-[140px]">{{ audience.name }}</div>
                        </Tooltip>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow
                    v-for="creative in selectedCreatives"
                    :key="`creative-row-${creative.token}`"
                    class="divide-x divide-border">
                    <TableCell class="sticky left-0 z-10 bg-background w-[220px] border-r py-5 px-2">
                      <div class="flex items-center gap-2">
                        <Checkbox
                          :model-value="checkboxState(null, creative.token)"
                          @update:model-value="(v) => toggle(v, null, creative.token)" />
                        <Tooltip :content="creative.name">
                          <div class="text-sm font-medium truncate">{{ creative.name }}</div>
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell
                      v-for="audience in selectedAudiences"
                      :key="`cell-${audience.token}-${creative.token}`"
                      class="text-center py-5">
                      <Checkbox
                        :model-value="checkboxState(audience.token, creative.token)"
                        @update:model-value="(v) => toggle(v, audience.token, creative.token)" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </form>
    </CardContent>
  </Card>
</template>
